// Meta Pixel Event Tracking Utility
// Pixel ID: 1953865535494055

const DEDUP_KEY_PREFIX = 'fbq_tracked_';

/**
 * Check if an event has already been tracked for a given unique key.
 * Prevents duplicate Purchase fires on page refresh / back-forward nav.
 */
const hasAlreadyTracked = (eventName, uniqueId) => {
  if (!uniqueId) return false;
  const key = `${DEDUP_KEY_PREFIX}${eventName}_${uniqueId}`;
  return sessionStorage.getItem(key) === 'true';
};

const markAsTracked = (eventName, uniqueId) => {
  if (!uniqueId) return;
  const key = `${DEDUP_KEY_PREFIX}${eventName}_${uniqueId}`;
  sessionStorage.setItem(key, 'true');
};

/**
 * Generate a unique event ID for Meta server-side deduplication
 */
const generateEventId = (prefix) => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Track a Meta Pixel event
 */
export const trackEvent = (eventName, params = {}, eventId = null) => {
  if (typeof window !== 'undefined' && window.fbq) {
    const options = eventId ? { eventID: eventId } : {};
    window.fbq('track', eventName, params, options);
  }
};

/**
 * Track PageView event (called automatically on page load)
 */
export const trackPageView = () => {
  trackEvent('PageView');
};

/**
 * Track Lead event - when a user shows interest (e.g., starts signup)
 */
export const trackLead = (params = {}) => {
  trackEvent('Lead', {
    content_name: params.content_name || 'AmbitionGPT Signup',
    content_category: params.content_category || 'AI Coaching',
    ...params
  });
};

/**
 * Track CompleteRegistration event - when signup is complete
 */
export const trackCompleteRegistration = (params = {}) => {
  trackEvent('CompleteRegistration', {
    content_name: params.content_name || 'AmbitionGPT Account',
    status: params.status || 'registered',
    ...params
  });
};

/**
 * Track InitiateCheckout event - when user starts checkout
 */
export const trackInitiateCheckout = (params = {}) => {
  trackEvent('InitiateCheckout', {
    value: parseFloat(params.value) || 37.00,
    currency: 'USD',
    content_name: params.content_name || 'AmbitionGPT — Monthly Membership',
    num_items: 1,
    ...params
  });
};

/**
 * Track Purchase event — fires ONCE per unique transaction.
 * Requires a `transaction_id` (Stripe session_id or PayPal paymentId) for deduplication.
 * Will silently skip if the same transaction_id has already been tracked in this session.
 */
export const trackPurchase = (params = {}) => {
  const txnId = params.transaction_id;

  // CRITICAL: Only fire once per transaction
  if (hasAlreadyTracked('Purchase', txnId)) {
    console.log('[MetaPixel] Purchase already tracked for:', txnId);
    return;
  }

  const value = parseFloat(params.value);
  if (!value || value <= 0) {
    console.warn('[MetaPixel] Skipping Purchase — invalid value:', params.value);
    return;
  }

  const eventId = generateEventId('purchase');

  trackEvent('Purchase', {
    value: value,
    currency: 'USD',
    content_name: params.content_name || 'AmbitionGPT — Monthly Membership',
    content_type: 'product',
    num_items: 1,
    order_id: txnId
  }, eventId);

  markAsTracked('Purchase', txnId);
  console.log('[MetaPixel] Purchase tracked:', { value, txnId, eventId });
};

/**
 * Track custom event
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
};

export default {
  trackEvent,
  trackPageView,
  trackLead,
  trackCompleteRegistration,
  trackInitiateCheckout,
  trackPurchase,
  trackCustomEvent
};
